/**
 * Pure helpers behind the client Activity tab — the timeline and the follow-ups.
 *
 * Kept out of the components for the same reason as `directoryFilters.ts`: the
 * rules that are easy to get subtly wrong (a task due today is not late; a
 * timeline groups on the day something HAPPENED, not the day it was typed) get
 * tests that pin them independently of the component tree.
 */

import type { InteractionResponse, TaskResponse } from '@biasafe.models';

// ── Interaction kinds ────────────────────────────────────────────────

/** Mirrors INTERACTION_KIND_VALUES in biasafe.database.models.client. */
export type InteractionKind = 'NOTE' | 'CALL' | 'MEETING' | 'EMAIL';

export const INTERACTION_KINDS: Array<{
  key: InteractionKind;
  label: string;
  /** Past-tense verb for the composer's confirmation copy. */
  verb: string;
}> = [
  { key: 'NOTE', label: 'Note', verb: 'Note added' },
  { key: 'CALL', label: 'Call', verb: 'Call logged' },
  { key: 'MEETING', label: 'Meeting', verb: 'Meeting logged' },
  { key: 'EMAIL', label: 'Email', verb: 'Email logged' },
];

type KindTone = { badge: string; dot: string };

// One colour per kind so a timeline is scannable without reading every entry.
// Deliberately NOT the status palette from clientStatus.tsx: emerald there
// means "Active client", and reusing it here would make a logged email look
// like a lifecycle state.
// Keyed by `string`, not `InteractionKind`, precisely so the lookup below is
// allowed to miss. Typing it by the union tells TypeScript every lookup
// succeeds, which is false the moment the backend ships a kind this build
// predates — and would make the fallback look like dead code.
const KIND_TONE: Record<string, KindTone> = {
  NOTE: {
    badge:
      'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/20',
    dot: 'bg-slate-400 dark:bg-slate-500',
  },
  CALL: {
    badge: 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20',
    dot: 'bg-sky-500 dark:bg-sky-400',
  },
  MEETING: {
    badge:
      'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/20',
    dot: 'bg-violet-500 dark:bg-violet-400',
  },
  EMAIL: {
    badge:
      'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20',
    dot: 'bg-amber-500 dark:bg-amber-400',
  },
};

/** Colours for a kind, falling back to NOTE for a kind this build predates.
 *
 * The backend stores the kind as a string precisely so a new one is a deploy
 * and not a migration, which means an older UI WILL meet a kind it has never
 * heard of. Rendering it in the note palette is wrong-ish; rendering `undefined`
 * into a className is a blank badge, which is worse. */
export function kindTone(kind: string): KindTone {
  return KIND_TONE[kind.toUpperCase()] ?? KIND_TONE.NOTE;
}

export function kindLabel(kind: string): string {
  const found = INTERACTION_KINDS.find((k) => k.key === kind.toUpperCase());
  // Title-case the unknown rather than showing raw UPPER_SNAKE.
  return found?.label ?? kind.charAt(0) + kind.slice(1).toLowerCase();
}

// ── Time ─────────────────────────────────────────────────────────────

/**
 * "Today" / "Yesterday" / "12 Mar 2026" for a timestamp.
 *
 * Used as the timeline's day heading, so it takes `now` explicitly: a heading
 * computed from a hidden `Date.now()` cannot be tested for the midnight
 * boundary, which is the only part of it anyone gets wrong.
 */
export function dayHeading(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown date';

  const startOfDay = (x: Date) =>
    new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** "09:41" — the time within the day heading's group. */
export function timeOfDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

export interface TimelineGroup {
  heading: string;
  entries: Array<InteractionResponse>;
}

/**
 * Group a timeline into day sections, preserving the server's order.
 *
 * Groups on `occurredAt` — when the thing HAPPENED — never `createdAt`. A call
 * that happened Tuesday and was written up Thursday belongs under Tuesday, and
 * grouping it under Thursday would put it above Wednesday's meeting in a list
 * the advisor is reading as a sequence of events.
 *
 * The server already sorts newest-first; this does NOT re-sort, so a change to
 * that order shows up here rather than being silently corrected.
 */
export function groupByDay(
  entries: Array<InteractionResponse>,
  now: Date = new Date(),
): Array<TimelineGroup> {
  return groupItemsByDay(entries, (e) => e.occurredAt, now).map((g) => ({
    heading: g.heading,
    entries: g.items,
  }));
}

/**
 * The same grouping, for anything with a timestamp.
 *
 * `groupByDay` above is typed to an interaction because that was the only
 * feed. The activity feed groups rows from a dozen sources with one `at`, and
 * a second copy of a loop whose only subtlety is the midnight boundary would
 * be a second place to get that boundary wrong. Same rules: groups on the
 * instant the caller names, preserves the server's order, never re-sorts.
 */
export function groupItemsByDay<T>(
  items: ReadonlyArray<T>,
  at: (item: T) => string,
  now: Date = new Date(),
): Array<{ heading: string; items: T[] }> {
  const out: Array<{ heading: string; items: T[] }> = [];
  for (const item of items) {
    const heading = dayHeading(at(item), now);
    const last = out.length > 0 ? out[out.length - 1] : undefined;
    if (last?.heading === heading) last.items.push(item);
    else out.push({ heading, items: [item] });
  }
  return out;
}

// ── Follow-ups ───────────────────────────────────────────────────────

export type DueTone = 'overdue' | 'today' | 'upcoming' | 'none';

/**
 * How a task's due date should read.
 *
 * `isOverdue` comes from the SERVER and is authoritative, but it is measured
 * against the day the browser SENT (`today` on the task list), because a due
 * date is a calendar day and only the advisor's own day makes it late. Measured
 * on the server's UTC clock, a task due today turned red at 20:00 in Toronto.
 * This only decides the remaining, cosmetic distinction between "due today"
 * and "due later".
 */
export function dueTone(task: TaskResponse, today: Date = new Date()): DueTone {
  if (task.isOverdue) return 'overdue';
  if (!task.dueDate) return 'none';
  const iso = toIsoDate(today);
  if (task.dueDate === iso) return 'today';
  return 'upcoming';
}

/** Local YYYY-MM-DD. Never `toISOString()`, which converts to UTC first and so
 * reports tomorrow's date for anyone east of Greenwich after their evening. */
export function toIsoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "Due 12 Mar 2026" / "Due today" / "No due date". tz-safe (parses the parts). */
export function dueLabel(task: TaskResponse, today: Date = new Date()): string {
  if (!task.dueDate) return 'No due date';
  if (task.dueDate === toIsoDate(today)) return 'Due today';
  const [y, m, d] = task.dueDate.split('-').map(Number);
  if (!y || !m || !d) return `Due ${task.dueDate}`;
  const label = new Date(y, m - 1, d).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  // "Overdue:" rather than "Overdue" and a dash: the house rule keeps em
  // dashes out of product copy, and this string is copy even though it is
  // computed rather than written in the JSX, which is exactly why a scan for
  // string literals in markup would not have found it.
  //
  // Staging reached the same line independently, through the sweep that went
  // through the whole tree. Both sides agree on the code; what survives the
  // merge is the reason, so the next person to shorten this does not put the
  // dash back.
  return `${task.isOverdue ? 'Overdue:' : 'Due'} ${label}`;
}

export const DUE_TONE_CLASS: Record<DueTone, string> = {
  overdue: 'text-red-600 dark:text-red-400',
  today: 'text-amber-600 dark:text-amber-400',
  upcoming: 'text-muted-foreground',
  none: 'text-muted-foreground',
};

/**
 * Who a task is on.
 *
 * Unassigned is a real state — the team queue before anyone picks it up — and
 * says so, rather than rendering an empty cell that reads as a loading failure.
 */
export function assigneeLabel(task: TaskResponse): string {
  return task.assigneeName?.trim() || 'Unassigned';
}

// ── Meetings ─────────────────────────────────────────────────────────

/** Mirrors MEETING_STATUS_VALUES in biasafe.database.models.client. */
export type MeetingStatus = 'SCHEDULED' | 'HELD' | 'CANCELLED';

/**
 * How a meeting reads on the agenda.
 *
 * `overdue` is the state worth naming: a meeting still SCHEDULED whose start
 * time has passed. Nobody closed it, and it is neither upcoming nor history —
 * left unlabelled it sits silently at the top of the list forever, which is how
 * an agenda stops meaning anything.
 */
export type MeetingTone = 'upcoming' | 'overdue' | 'held' | 'cancelled';

export function meetingTone(
  status: string,
  startsAt: string,
  now: Date = new Date(),
): MeetingTone {
  if (status === 'HELD') return 'held';
  if (status === 'CANCELLED') return 'cancelled';
  const start = new Date(startsAt).getTime();
  if (Number.isNaN(start)) return 'upcoming';
  return start < now.getTime() ? 'overdue' : 'upcoming';
}

export const MEETING_TONE_CLASS: Record<MeetingTone, string> = {
  upcoming: 'text-foreground',
  overdue: 'text-amber-600 dark:text-amber-400',
  held: 'text-muted-foreground',
  cancelled: 'text-muted-foreground',
};

/**
 * "Tue 1 Sep, 15:00–16:00" for a meeting window.
 *
 * The end time drops its date when the meeting does not cross midnight, which
 * is almost always — repeating it reads as two separate days.
 */
export function meetingWhen(
  startsAt: string,
  endsAt: string,
  now: Date = new Date(),
): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  if (Number.isNaN(start.getTime())) return 'Unknown time';

  const day = dayHeading(startsAt, now);
  const from = start.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
  if (Number.isNaN(end.getTime())) return `${day}, ${from}`;

  const sameDay = start.toDateString() === end.toDateString();
  const to = end.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
  if (!sameDay) {
    return `${day}, ${from} → ${dayHeading(endsAt, now)}, ${to}`;
  }
  // A zero-length meeting is a reminder, not a window; showing "15:00–15:00"
  // reads as a bug rather than as the instant it is.
  return from === to ? `${day}, ${from}` : `${day}, ${from}–${to}`;
}

/** Whether this meeting has a calendar event behind it. */
export function hasCalendarLink(
  provider: string | null,
  eventId: string | null,
) {
  return Boolean(provider && eventId);
}

/**
 * A stored instant as the value an `<input type="datetime-local">` expects:
 * "2026-09-01T15:00", in the browser's own zone.
 *
 * Never `toISOString().slice(0, 16)`, which is the UTC wall clock: the editor
 * would open a 15:00 Toronto meeting at 19:00, and saving it untouched would
 * move it four hours.
 */
export function toDateTimeLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${toIsoDate(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Whole minutes between a meeting's start and end, never negative. */
export function meetingMinutes(startsAt: string, endsAt: string): number {
  const ms = new Date(endsAt).getTime() - new Date(startsAt).getTime();
  if (Number.isNaN(ms)) return 60;
  return Math.max(0, Math.round(ms / 60_000));
}
